import { type FC } from "react";
import { MagnifyingGlass } from "@/components/icons";
import TrendingSection from "./TrendingSection";
import LearnerFavoritesSection from "./LearnerFavoritesSection";
import { ProductSearchListItemProps } from "./ProductSearchListItem";
import useTranslations from "@/i18n/useTranslations";

type CategoryLink = {
  label: string;
  url: string;
};

interface Props {
  featuredCategories?: CategoryLink[];
  featuredItems?: ProductSearchListItemProps[];
  trendingSectionHeader?: string;
  mobileCategoryLimit?: number;
  learnerFavoritesSectionHeader?: string;
  noResultsHeader?: string;
  noResultsBullets?: string[];
  location?: string;
}

const NoResultsState: FC<Props> = ({
  featuredCategories = [],
  featuredItems = [],
  trendingSectionHeader,
  mobileCategoryLimit,
  learnerFavoritesSectionHeader,
  noResultsHeader,
  noResultsBullets,
  location = "embedded-search",
}) => {
  const t = useTranslations("Search");
  const header = noResultsHeader ?? t("noResultsFound");
  const bullets = noResultsBullets ?? [
    t("checkSpelling"),
    t("useMoreGeneralTerms"),
    t("browseByCategoryBelow"),
  ];
  return (
    <div className="w-full flex flex-col gap-4">
      <div
        className="flex flex-col justify-center items-start gap-y-4 pb-4 border-b border-putty-400"
        role="status"
      >
        <span className="text-md font-bold text-[#00262B] flex items-start gap-2.5 self-stretch">
          <MagnifyingGlass /> {header}
        </span>
        <ul className="hidden md:flex items-start gap-4 self-stretch list-none text-putty-700">
          {bullets.map((bullet) => (
            <li
              key={bullet}
              className="text-xs font-bold before:content-['•'] before:mr-1.5 before:inline-block"
            >
              {bullet}
            </li>
          ))}
        </ul>
      </div>

      {featuredCategories.length > 0 && (
        <TrendingSection
          categories={featuredCategories}
          sectionHeader={trendingSectionHeader}
          mobileCategoryLimit={mobileCategoryLimit}
          location={location}
        />
      )}

      <LearnerFavoritesSection
        items={featuredItems}
        sectionHeader={learnerFavoritesSectionHeader}
        location={location}
      />
    </div>
  );
};

export default NoResultsState;
